/**
 * Primitivos compartilhados por todo contrato da API.
 *
 * Fontes: `docs/API.md §1.2`–`§1.8` (cabecalhos, paginacao, ordenacao, datas e dinheiro),
 * `docs/CONTRACTS.md §7.1` (dinheiro) e `§7.2` (nomes canonicos), `docs/DATA_MODEL.md`
 * (tipos de coluna) e `docs/FLEET.md §2` (identidade da instancia).
 *
 * Principio deste arquivo: **validacao de verdade, nao regex frouxa**. CPF confere digito
 * verificador; telefone confere DDD existente e a regra do nono digito; data confere calendario.
 * Um schema que aceita `000.000.000-00` nao esta validando CPF, esta contando caracteres — e a
 * unica coisa que ele garante e que o dado sujo chega mais fundo no sistema.
 */
import { z } from 'zod';

// ---------------------------------------------------------------------------
// Dinheiro — CONTRACTS §7.1 (encerra X-04)
// ---------------------------------------------------------------------------

/**
 * `Cents` e **inteiro em centavos**, com marca de tipo para que um `number` solto nao entre por
 * engano. **Sem `bigint`**: nao serializa em JSON e o teto do negocio esta 10 ordens de grandeza
 * abaixo de `MAX_SAFE_INTEGER`.
 *
 * Float em dinheiro nao e imprecisao aceitavel aqui: a comparacao com o teto CMED (REG-032)
 * precisa ser exata, e centavo de diferenca em medicamento e nao-conformidade, nao arredondamento.
 */
export type Cents = number & { readonly __brand: 'Cents' };

export const CentsSchema = z
  .number()
  .int('Dinheiro é inteiro em centavos; valor fracionário é bug (CONTRACTS §7.1).')
  .safe()
  .transform((n) => n as Cents);

/** Centavos que nao podem ser negativos (preco, subtotal, frete, limite). */
export const NonNegativeCentsSchema = z
  .number()
  .int('Dinheiro é inteiro em centavos (CONTRACTS §7.1).')
  .nonnegative()
  .safe()
  .transform((n) => n as Cents);

/** Unica moeda do produto. */
export const CurrencySchema = z.literal('BRL');
export type Currency = z.infer<typeof CurrencySchema>;

/** `Money` canonico (CONTRACTS §7.1, DOMAIN §3). */
export const MoneySchema = z.object({
  amountCents: CentsSchema,
  currency: CurrencySchema,
});
export type Money = z.infer<typeof MoneySchema>;

export const NonNegativeMoneySchema = z.object({
  amountCents: NonNegativeCentsSchema,
  currency: CurrencySchema,
});

/**
 * Percentual em **pontos-base** inteiros: `1500` = 15,00% (API §1.8). Evita a ambiguidade
 * `0.15` vs `15` que ja custou um bug de desconto em todo projeto que nao a resolveu.
 */
export const DiscountBpsSchema = z
  .number()
  .int('Percentual é inteiro em pontos-base (API §1.8).')
  .min(0)
  .max(10_000);
export type DiscountBps = z.infer<typeof DiscountBpsSchema>;

// ---------------------------------------------------------------------------
// Identificadores
// ---------------------------------------------------------------------------

const UUID_V7_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * **UUIDv7** — identificador publico de recurso (API §1.3). Ordenavel no tempo, sem expor
 * contagem. Nao e `z.string().uuid()`: aquele aceitaria v4, e v4 num `id` de pedido significa
 * que alguem trocou o gerador e perdeu a localidade de indice (DATA_MODEL §PREMISSA 12).
 */
export const Uuidv7Schema = z
  .string()
  .regex(UUID_V7_RE, 'Identificador deve ser UUIDv7 (API §1.3).');
export type Uuidv7 = z.infer<typeof Uuidv7Schema>;

/** Crockford base32, sem I, L, O e U. */
const ULID_RE = /^[0-7][0-9ABCDEFGHJKMNPQRSTVWXYZ]{25}$/;

/**
 * **ULID** de 26 caracteres — usado pelo `instanceId` da frota (FLEET §2, IMPLANTACAO §2.2,
 * ADR-0019). Note o escopo: `instanceId` e **propriedade de ambiente, nao coluna de tabela**;
 * nao e `tenantId` e nao entra no modelo de dados. Identificador de recurso e UUIDv7.
 *
 * O primeiro caractere e limitado a `0..7` porque o timestamp de 48 bits nao pode transbordar
 * os 26 caracteres do encoding.
 */
export const UlidSchema = z
  .string()
  .length(26)
  .regex(ULID_RE, 'ULID inválido: 26 caracteres em Crockford base32 (FLEET §2).');
export type Ulid = z.infer<typeof UlidSchema>;

/** Chave humana do cliente na frota (ADR-0020 §36). */
export const ClientSlugSchema = z
  .string()
  .regex(/^[a-z][a-z0-9-]{2,30}$/, 'clientSlug inválido (ADR-0020).');
export type ClientSlug = z.infer<typeof ClientSlugSchema>;

/**
 * Codigo curto e legivel do pedido, para o balcao e o WhatsApp (API §1.3, ex.: `SOL-4821`).
 * O prefixo e por instancia — nao presuma `SOL`, que e do piloto.
 */
export const OrderCodeSchema = z
  .string()
  .regex(/^[A-Z]{2,5}-\d{4,8}$/, 'Código de pedido inválido (API §1.3).');
export type OrderCode = z.infer<typeof OrderCodeSchema>;

// ---------------------------------------------------------------------------
// Data e hora — API §1.8
// ---------------------------------------------------------------------------

/**
 * `Instant`: ISO-8601 **com offset explicito**. Nunca epoch, nunca `dd/mm/yyyy` no wire.
 * Persistencia e em UTC (`timestamptz`), mas todo corte de dia — fechamento de caixa, janela de
 * silencio de notificacao (REG-074), SLA de aceite — e calculado em `America/Fortaleza`
 * (UTC-03:00, sem horario de verao), nunca em UTC.
 */
export const InstantSchema = z
  .string()
  .regex(
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,9})?(Z|[+-]\d{2}:\d{2})$/,
    'Instante deve ser ISO-8601 com offset explícito (API §1.8).',
  )
  .refine((s) => !Number.isNaN(Date.parse(s)), 'Instante não corresponde a uma data real.');
export type Instant = z.infer<typeof InstantSchema>;

/** Fuso de negocio, unico e fixo (API §1.8). */
export const BUSINESS_TIME_ZONE = 'America/Fortaleza' as const;

/** Verifica calendario de verdade: `2026-02-30` e `2025-02-29` sao recusados. */
function isRealCalendarDate(value: string): boolean {
  const parts = value.split('-');
  const year = Number(parts[0]);
  const month = Number(parts[1]);
  const day = Number(parts[2]);
  if (month < 1 || month > 12 || day < 1) return false;
  // Dia 0 do mes seguinte = ultimo dia deste mes. UTC evita deslocamento por fuso local.
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return day <= lastDay;
}

/** `LocalDate`: data de calendario sem hora (validade de receita, dia de fechamento de caixa). */
export const LocalDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Data deve estar no formato AAAA-MM-DD (API §1.8).')
  .refine(isRealCalendarDate, 'Data inexistente no calendário.');
export type LocalDate = z.infer<typeof LocalDateSchema>;

// ---------------------------------------------------------------------------
// Telefone BR — E.164
// ---------------------------------------------------------------------------

/**
 * DDDs existentes no Plano Nacional de Numeracao (Anatel). A lista fechada importa porque o
 * login do cliente e **telefone + OTP** (CONTRACTS §5.9): DDD inexistente vira SMS pago para
 * lugar nenhum e uma conta que ninguem consegue recuperar.
 */
export const BR_AREA_CODES: readonly number[] = [
  11, 12, 13, 14, 15, 16, 17, 18, 19, 21, 22, 24, 27, 28, 31, 32, 33, 34, 35, 37, 38, 41, 42, 43,
  44, 45, 46, 47, 48, 49, 51, 53, 54, 55, 61, 62, 63, 64, 65, 66, 67, 68, 69, 71, 73, 74, 75, 77,
  79, 81, 82, 83, 84, 85, 86, 87, 88, 89, 91, 92, 93, 94, 95, 96, 97, 98, 99,
];

const BR_AREA_CODE_SET = new Set(BR_AREA_CODES);

/**
 * Telefone brasileiro em **E.164 sem espacos**: `+5588999998888` (API §6.20, DATA_MODEL
 * `phoneE164 VarChar(16)`).
 *
 * Regras aplicadas, e nao apenas o comprimento:
 * - prefixo `+55`;
 * - DDD tem de existir (`BR_AREA_CODES`);
 * - assinante de **9 digitos** e movel e tem de comecar com `9` — celular brasileiro nao comeca
 *   com outro digito desde a migracao do nono digito;
 * - assinante de **8 digitos** e fixo e comeca em `2..5`.
 */
export const PhoneBrE164Schema = z
  .string()
  .regex(/^\+55\d{10,11}$/, 'Telefone deve estar em E.164: +55 seguido de DDD e número.')
  .superRefine((value, ctx) => {
    const digits = value.slice(3);
    const areaCode = Number(digits.slice(0, 2));
    if (!BR_AREA_CODE_SET.has(areaCode)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: `DDD ${areaCode} não existe.` });
      return;
    }
    const subscriber = digits.slice(2);
    if (subscriber.length === 9) {
      if (!subscriber.startsWith('9')) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Celular com 9 dígitos precisa começar com 9.',
        });
      }
      return;
    }
    const first = subscriber.charAt(0);
    if (first < '2' || first > '5') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Telefone fixo com 8 dígitos precisa começar entre 2 e 5.',
      });
    }
  });
export type PhoneBrE164 = z.infer<typeof PhoneBrE164Schema>;

/** Normaliza entrada humana (`(88) 99999-8888`) para E.164. Nao valida — so formata. */
export function toE164Br(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  const national = digits.startsWith('55') && digits.length > 11 ? digits.slice(2) : digits;
  return `+55${national}`;
}

// ---------------------------------------------------------------------------
// CPF
// ---------------------------------------------------------------------------

/**
 * Digitos verificadores do CPF (modulo 11). Recusa tambem as 10 sequencias de digito repetido
 * (`00000000000`, `11111111111`, ...), que passam na aritmetica mas nao sao CPF de ninguem —
 * e sao exatamente o que um formulario recebe quando alguem quer pular o campo.
 */
export function isValidCpf(digits: string): boolean {
  if (!/^\d{11}$/.test(digits)) return false;
  if (/^(\d)\1{10}$/.test(digits)) return false;

  const checkDigit = (length: number): number => {
    let sum = 0;
    for (let i = 0; i < length; i += 1) {
      sum += Number(digits.charAt(i)) * (length + 1 - i);
    }
    const remainder = (sum * 10) % 11;
    return remainder === 10 ? 0 : remainder;
  };

  return checkDigit(9) === Number(digits.charAt(9)) && checkDigit(10) === Number(digits.charAt(10));
}

/**
 * CPF **so digitos**, ja validado. O wire nunca carrega mascara: pontuacao e formatacao do
 * cliente (API §1.8).
 *
 * Nota de LGPD: CPF e `@pii{c:DIRECT}`. Onde ele aparecer num contrato, o campo tambem carrega a
 * base legal — o schema valida a forma, nao autoriza a coleta.
 */
export const CpfSchema = z
  .string()
  .regex(/^\d{11}$/, 'CPF deve ter 11 dígitos, sem pontuação.')
  .refine(isValidCpf, 'CPF inválido: dígito verificador não confere.');
export type Cpf = z.infer<typeof CpfSchema>;

/** Remove mascara antes de validar. */
export function stripCpfMask(raw: string): string {
  return raw.replace(/\D/g, '');
}

// ---------------------------------------------------------------------------
// CEP
// ---------------------------------------------------------------------------

/**
 * CEP com **8 digitos, sem hifen** (DATA_MODEL `postalCode VarChar(8)`).
 *
 * Escopo, que aqui vale mais que o formato: CEP e **informativo e nunca chave** (R-28). O frete
 * vem de `delivery_zone`, escolhida em lista fechada, e o campo que o entregador usa e
 * `reference` — obrigatorio, ao contrario deste. Ha municipio com CEP unico, e por isso
 * `fulfillment.requirePostalCode` e `false` por default.
 */
export const CepSchema = z
  .string()
  .regex(/^\d{8}$/, 'CEP deve ter 8 dígitos, sem hífen.')
  .refine((v) => v !== '00000000', 'CEP inexistente.');
export type Cep = z.infer<typeof CepSchema>;

export function stripCepMask(raw: string): string {
  return raw.replace(/\D/g, '');
}

// ---------------------------------------------------------------------------
// Paginacao por cursor — API §1.4
// ---------------------------------------------------------------------------

/**
 * O cursor e **opaco** para o cliente: base64url de `{k, i, s, h}`, com HMAC de 8 bytes. O HMAC
 * existe para impedir que o cliente forje um cursor e pule filtro de autorizacao ou de classe
 * regulatoria — o schema aqui so garante o alfabeto; quem verifica a assinatura e o servidor.
 *
 * Cursor antigo com filtro novo devolve `INVALID_CURSOR` (400), nunca resultado silenciosamente
 * errado.
 */
export const CursorSchema = z
  .string()
  .min(1)
  .max(512)
  .regex(/^[A-Za-z0-9_-]+$/, 'Cursor deve ser base64url opaco (API §1.4).');
export type Cursor = z.infer<typeof CursorSchema>;

/** Limites por recurso (API §1.4). O maximo real e validado por rota; 100 e o teto geral. */
export const PAGE_LIMIT_DEFAULT = 24 as const;
export const PAGE_LIMIT_MAX_CATALOG = 100 as const;
export const PAGE_LIMIT_MAX_ORDERS = 50 as const;
export const PAGE_LIMIT_MAX_STORE_ORDERS = 200 as const;

export const PageLimitSchema = z.coerce.number().int().min(1).max(200);

/** Query de paginacao. `offset` foi **descartado** (API §1.4): duplica e pula item entre paginas. */
export const CursorPageQuerySchema = z.object({
  cursor: CursorSchema.optional(),
  limit: PageLimitSchema.default(PAGE_LIMIT_DEFAULT),
});
export type CursorPageQuery = z.infer<typeof CursorPageQuerySchema>;

/** Bloco `page` da resposta de colecao. */
export const PageInfoSchema = z.object({
  /** `null` na ultima pagina. */
  nextCursor: CursorSchema.nullable(),
  hasMore: z.boolean(),
  limit: z.number().int().min(1).max(200),
});
export type PageInfo = z.infer<typeof PageInfoSchema>;

/**
 * Envelope de **colecao** — `{ data, page }`. Recurso unico **nao tem envelope** (API §1.1):
 * envelope universal desperdica o status HTTP e obriga o cliente a inspecionar o corpo para
 * saber se deu certo.
 */
export function paginated<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    data: z.array(item),
    page: PageInfoSchema,
  });
}

/** `sort=<campo>` ou `sort=-<campo>` (API §1.5). A whitelist de campos e por recurso. */
export const SortParamSchema = z
  .string()
  .regex(/^-?[a-zA-Z][a-zA-Z0-9]*$/, 'Ordenação inválida (API §1.5).');

export function parseSortParam(value: string): { field: string; descending: boolean } {
  const descending = value.startsWith('-');
  return { field: descending ? value.slice(1) : value, descending };
}

// ---------------------------------------------------------------------------
// Concorrencia e idempotencia — API §3, §4 e CONTRACTS §7.2
// ---------------------------------------------------------------------------

/**
 * `Idempotency-Key`. Escopo `(actorId, method, path, key)` + `requestFingerprint` (X-08).
 *
 * **UUID gerado pelo cliente** (`API §3.2`), e nao "qualquer string de 8 a 255". A regra tem
 * motivo de seguranca escrito no documento: *"chave previsivel permite que um atacante
 * 'reserve' a resposta de outro usuario"*. O `min(8).max(255)` que esta linha tinha antes
 * aceitava `pedido-1` — o exemplo canonico de chave previsivel.
 *
 * A forma aceita e a mesma que `apps/api` ja aplicava no interceptor de idempotencia
 * (versoes 1..8, variante RFC 4122), para que o schema e a rota nao discordem. O cliente
 * **gera v4** (`crypto.randomUUID()`); a faixa mais larga existe porque uma chave v7 vinda de
 * um gerador de id do proprio produto tambem e legitima.
 */
export const IdempotencyKeySchema = z
  .string()
  .regex(
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i,
    'Idempotency-Key deve ser um UUID gerado pelo cliente (API §3.2).',
  );
export type IdempotencyKey = z.infer<typeof IdempotencyKeySchema>;

/**
 * `ETag` fraco de carrinho: `W/"cart-<id>-<version>"` (CONTRACTS §7.2). So a mutacao do cliente
 * incrementa `version`; so o sync do ERP muda o `pricingSnapshotId`.
 */
export const WeakETagSchema = z.string().regex(/^W\/"[\x21\x23-\x7E]+"$/, 'ETag fraco inválido.');

/** Versao de lock otimista (`cart.version`, `order.version`). */
export const OptimisticVersionSchema = z.number().int().nonnegative();

// ---------------------------------------------------------------------------
// Outros primitivos citados nos contratos
// ---------------------------------------------------------------------------

/** Quantidade de item. O teto real por produto vem da politica regulatoria (REG-013). */
export const QuantitySchema = z.number().int().min(1);

/**
 * CRF do farmaceutico responsavel — `dispensedByPharmacistCrf` (REG-005). Formato
 * `CRF-<UF> <numero>`; a verificacao de vigencia e do dominio, nao do schema.
 */
export const CrfSchema = z
  .string()
  .regex(/^CRF-[A-Z]{2}\s?\d{3,8}$/, 'CRF inválido: esperado CRF-UF seguido do número.');

/** UFs brasileiras. */
export const UF_VALUES = [
  'AC',
  'AL',
  'AP',
  'AM',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MT',
  'MS',
  'MG',
  'PA',
  'PB',
  'PR',
  'PE',
  'PI',
  'RJ',
  'RN',
  'RS',
  'RO',
  'RR',
  'SC',
  'SP',
  'SE',
  'TO',
] as const;
export const UfSchema = z.enum(UF_VALUES);
export type Uf = z.infer<typeof UfSchema>;

/** Slug de recurso publico (categoria, marca, produto). `kebab-case` (API §1.3). */
export const SlugSchema = z
  .string()
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Slug deve ser kebab-case (API §1.3).')
  .max(120);
